// src/utils/ratingCalculations.js

// 상수 정의
export const INITIAL_RATING = 1500;  // 모든 선수의 시작 레이팅
export const K_FACTOR = 32;          // 단식 K-Factor
export const K_FACTOR_DOUBLES = 20;  // 복식 K-Factor

/**
 * 스코어 보정 계수 (M) 계산
 * 점수 차이가 클수록 승리의 가치가 높아짐
 * @param {number} winnerScore - 승자 점수
 * @param {number} loserScore - 패자 점수
 * @param {string} scoreType - 점수 타입 ('game' | 'tiebreak')
 * @returns {number} 스코어 보정 계수 (0.7 ~ 1.3)
 */
export const getScoreMultiplier = (winnerScore, loserScore, scoreType = 'tiebreak') => {
  if (winnerScore == null || loserScore == null) return 1.0;
  const diff = winnerScore - loserScore;

  if (scoreType === 'game') {
    // 게임 스코어: 4게임(4:0~4:3) 또는 6게임(6:0~6:4)
    if (diff >= 4) return 1.3;   // 완승 (6:0, 6:1, 6:2, 4:0)
    if (diff >= 2) return 1.0;   // 일반 (6:3, 6:4, 4:1, 4:2)
    return 0.7;                   // 접전 (4:3)
  }

  // 타이브레이크 스코어: 7점 기준 (7:0 ~ 7:6)
  if (diff >= 5) return 1.3;   // 완승 (7:0 ~ 7:2)
  if (diff >= 3) return 1.0;   // 일반 (7:3 ~ 7:4)
  return 0.7;                   // 접전 (7:5 ~ 7:6)
};

/**
 * 최근 폼 보정 계수 (R) 계산
 * 최근 5경기의 기대 대비 실제 성적으로 판단
 * @param {Array} recentMatches - 최근 경기 배열 [{expectedScore, actualScore}, ...]
 * @returns {number} 최근 폼 보정 계수 (1.0 또는 1.2)
 */
export const getRecencyMultiplier = (recentMatches) => {
  if (!recentMatches || recentMatches.length < 3) return 1.0;

  const totalExpected = recentMatches.reduce((sum, m) => sum + m.expectedScore, 0);
  const totalActual = recentMatches.reduce((sum, m) => sum + m.actualScore, 0);

  if (totalExpected === 0) return 1.0;
  const ratio = totalActual / totalExpected;

  // 기대 대비 30% 이상 초과/부진이면 가속
  if (ratio >= 1.3 || ratio <= 0.7) return 1.2;
  return 1.0;
};

/**
 * Modified Elo 레이팅 계산 함수
 * 새 Elo = 기존Elo + K × M × R × (실제결과 - 기대승률)
 * @param {number} player1Rating - 선수1의 현재 레이팅
 * @param {number} player2Rating - 선수2의 현재 레이팅
 * @param {boolean} player1Won - 선수1의 승리 여부
 * @param {object} options - 추가 옵션
 * @param {number} options.winnerScore - 승자 점수 (스코어 보정용)
 * @param {number} options.loserScore - 패자 점수 (스코어 보정용)
 * @param {Array} options.player1RecentMatches - 선수1 최근 경기 기록
 * @param {Array} options.player2RecentMatches - 선수2 최근 경기 기록
 * @param {boolean} options.isDoubles - 복식 여부
 * @returns {object} 새로운 레이팅 값
 */
export const calculateNewRatings = (player1Rating, player2Rating, player1Won, options = {}) => {
  const {
    winnerScore,
    loserScore,
    player1RecentMatches,
    player2RecentMatches,
    isDoubles = false,
    scoreType = 'tiebreak',
  } = options;

  const baseK = isDoubles ? K_FACTOR_DOUBLES : K_FACTOR;
  const M = getScoreMultiplier(winnerScore, loserScore, scoreType);

  const expectedScore1 = 1 / (1 + Math.pow(10, (player2Rating - player1Rating) / 400));
  const expectedScore2 = 1 / (1 + Math.pow(10, (player1Rating - player2Rating) / 400));

  const actualScore1 = player1Won ? 1 : 0;
  const actualScore2 = player1Won ? 0 : 1;

  const R1 = getRecencyMultiplier(player1RecentMatches);
  const R2 = getRecencyMultiplier(player2RecentMatches);

  const newRating1 = Math.max(0, Math.round(player1Rating + baseK * M * R1 * (actualScore1 - expectedScore1)));
  const newRating2 = Math.max(0, Math.round(player2Rating + baseK * M * R2 * (actualScore2 - expectedScore2)));

  return { newRating1, newRating2 };
};

/**
 * 동적 K-Factor 계산 (선택적 - 향후 사용 가능)
 * 경기 수가 적은 선수는 더 큰 변화를 적용
 * @param {number} matchCount - 선수의 총 경기 수
 * @returns {number} K-Factor 값
 */
export const getDynamicKFactor = (matchCount) => {
  if (matchCount < 10) return 40;      // 초보자: 큰 변화
  if (matchCount < 30) return 32;      // 중급자: 보통 변화
  return 24;                            // 숙련자: 작은 변화
};

/**
 * 예상 승률 계산
 * @param {number} playerRating - 선수의 레이팅
 * @param {number} opponentRating - 상대의 레이팅
 * @returns {number} 예상 승률 (0~1)
 */
export const getExpectedScore = (playerRating, opponentRating) => {
  return 1 / (1 + Math.pow(10, (opponentRating - playerRating) / 400));
};

/**
 * 승률 기반 예상 레이팅 차이 계산
 * @param {number} winProbability - 목표 승률 (0~1)
 * @returns {number} 필요한 레이팅 차이
 */
export const getRatingDifferenceForWinRate = (winProbability) => {
  if (winProbability <= 0 || winProbability >= 1) return 0;
  return Math.round(400 * Math.log10(winProbability / (1 - winProbability)));
};